// =============================================================================
// Altara Shared Formatters — Single Source of Truth
// =============================================================================
// PURPOSE: All number/currency/percent formatting lives here.
// NEVER define local formatCurrency/formatDollar functions in components.
// See docs/REF_design_tokens.md for the formatting rules.
// =============================================================================

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("en-US");

// =============================================================================
// DISPLAY CURRENCY (per-user: USD ⇄ AUD / SGD / …)
// =============================================================================
// All money in the DB is USD (Dynamis banks a USD Wise balance; no per-row
// currency column exists). This is a DISPLAY-ONLY multiply — it never touches
// stored data, never writes back, and the symbol stays "$" in EVERY currency.
// Which currency the "$" means is communicated by the sidebar toggle, not by the
// symbol (Mako 2026-07-15: "just show the $ amount, the toggle tells you").
//
// CurrencyContext is the React owner: it resolves the active code from the
// logged-in user's profiles.display_currency and calls setDisplayCurrency() to
// mirror it here, so the ~189 existing formatUSD/formatCompact call sites convert
// with ZERO edits. Re-render is IN PLACE via useSyncExternalStore + PageWrapper
// cloneElement — no remount, no chart re-animation, scroll preserved.
//
// USD is canonical (rate 1). Any non-USD code multiplies by its rate; an unknown
// code or bad rate degrades safely to USD. DELIBERATELY NOT converted:
// invoiceFormat.ts formatCurrency (billing/legal stays USD),
// formatPercent/formatNumber/formatCount (not money).
let _currencyCode = "USD";
let _rate = 1;

// Tiny external store so components can re-render IN PLACE on a currency change
// (via useSyncExternalStore) — no page remount, no chart re-animation, scroll
// preserved. The snapshot is a stable primitive string ("USD:1" / "SGD:1.29")
// so useSyncExternalStore never loops.
type CurrencyListener = () => void;
const _currencyListeners = new Set<CurrencyListener>();
let _currencySnapshot = "USD:1";

/**
 * Set the active display currency code + its USD→code rate, then notify
 * subscribers so every money component re-renders in place. rate is treated as 1
 * (no conversion) for USD, an unknown code, or a non-positive/non-finite rate.
 */
export function setDisplayCurrency(code: string, rate: number): void {
  _currencyCode = code || "USD";
  _rate = _currencyCode !== "USD" && Number.isFinite(rate) && rate > 0 ? rate : 1;
  const next = `${_currencyCode}:${_rate}`;
  if (next !== _currencySnapshot) {
    _currencySnapshot = next;
    _currencyListeners.forEach((l) => l());
  }
}

/** Subscribe to currency changes (for useSyncExternalStore). */
export function subscribeCurrency(cb: CurrencyListener): () => void {
  _currencyListeners.add(cb);
  return () => {
    _currencyListeners.delete(cb);
  };
}

/** Current currency snapshot ("CODE:rate", stable primitive for useSyncExternalStore). */
export function getCurrencySnapshot(): string {
  return _currencySnapshot;
}

/** Current code/rate mirror — for non-React reads (charts computing axis domains). */
export function getDisplayCurrency(): { code: string; rate: number } {
  return { code: _currencyCode, rate: _rate };
}

/**
 * Convert a USD amount to the active display currency. Returns n unchanged in
 * USD. Use this in any local/compact money formatter that does NOT already go
 * through formatUSD/formatCompact, so the toggle covers EVERY dollar.
 */
export function toDisplayCurrency(n: number): number {
  return _currencyCode === "USD" ? n : n * _rate;
}

/**
 * Format as whole-dollar USD: $1,234 (or AUD-converted when the toggle is on).
 * Default for all dashboard numbers, tables, cards.
 */
export function formatUSD(n: number): string {
  return usdFormatter.format(toDisplayCurrency(n));
}

/**
 * Format as 2-decimal USD: $1,234.56
 * Invoices and PDFs only — legal/billing precision.
 * Re-exports from invoiceFormat.ts for convenience.
 */
export { formatCurrency as formatUSDInvoice } from "./invoiceFormat";

/**
 * Format as compact USD: $1.2M, $12.3K, $950
 * For hero cards, chart labels, space-constrained contexts.
 * Strips trailing .0 — shows $12K not $12.0K.
 */
export function formatCompact(n: number): string {
  const converted = toDisplayCurrency(n);
  const abs = Math.abs(converted);
  const sign = converted < 0 ? "-" : "";

  if (abs >= 1_000_000) {
    const val = (abs / 1_000_000).toFixed(1);
    const clean = val.endsWith(".0") ? val.slice(0, -2) : val;
    return `${sign}$${clean}M`;
  }
  if (abs >= 1_000) {
    const val = (abs / 1_000).toFixed(1);
    const clean = val.endsWith(".0") ? val.slice(0, -2) : val;
    return `${sign}$${clean}K`;
  }
  return `${sign}$${Math.round(abs)}`;
}

/**
 * Format a percentage: 34.5% (default 1 decimal)
 * Pass decimals=0 for integer display: 35%
 * Input should be 0-100 range (not 0-1 ratio).
 */
export function formatPercent(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Percentage, or an em-dash when the denominator is 0 (no divide-by-zero, no
 * "NaN%"). Takes a raw numerator/denominator pair (NOT a pre-scaled ratio) and
 * scales to formatPercent's expected 0-100 range internally. For rate tiles that
 * must render a clean "—" on an empty population (e.g. a setter with 0 engaged
 * leads this week). Lives here so the one definition is shared and unit-testable.
 */
export function pctOrDash(num: number, den: number): string {
  return den === 0 ? "—" : formatPercent((num / den) * 100, 1);
}

/**
 * Format a plain number with commas: 1,234
 * For non-currency counts (calls, leads, messages).
 */
export function formatNumber(n: number): string {
  return numberFormatter.format(n);
}

/**
 * Format as compact count: 1.2M, 12.3K, 950
 * Mirrors formatCompact shape minus the $ prefix.
 * For KPI tiles, chart axes, anywhere a non-currency count needs K/M compaction.
 */
export function formatCount(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";

  if (abs >= 1_000_000) {
    const val = (abs / 1_000_000).toFixed(1);
    const clean = val.endsWith(".0") ? val.slice(0, -2) : val;
    return `${sign}${clean}M`;
  }
  if (abs >= 1_000) {
    const val = (abs / 1_000).toFixed(1);
    const clean = val.endsWith(".0") ? val.slice(0, -2) : val;
    return `${sign}${clean}K`;
  }
  return `${sign}${Math.round(abs)}`;
}

/**
 * Format a date as M/D/YYYY — locale-independent.
 * CRITICAL: Never use bare .toLocaleDateString() — Australian/European locales
 * produce DD/MM/YYYY which breaks chart X-axis parsing (day "01" → "January").
 * Always use this function or pass "en-US" explicitly.
 */
const dateShortFormatter = new Intl.DateTimeFormat("en-US", {
  month: "numeric",
  day: "numeric",
  year: "numeric",
});

/**
 * Format a Date as M/D/YYYY. Forces en-US to prevent locale-dependent DD/MM/YYYY.
 * Optional tz param: if provided, formats in that timezone instead of browser-local.
 */
export function formatDateShort(d: Date, tz?: string): string {
  if (tz) {
    return new Intl.DateTimeFormat("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      timeZone: tz,
    }).format(d);
  }
  return dateShortFormatter.format(d);
}

/**
 * Format a Date as YYYY-MM-DD. Locale-independent; safe for Supabase RPC date params.
 */
export function formatYYYYMMDD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Format a date as relative time: "2h ago", "Yesterday", "3 days ago".
 * Used for signal freshness indicators (e.g., "Last data: 2h ago").
 */
export function formatTimeAgo(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  return `${diffDays} days ago`;
}

/**
 * A duration in whole minutes as hours + minutes, never seconds, never days.
 * Mako 2026-10-09 on the Typeform journey: "pure minutes is hard to divide for
 * people ... minutes and hours. just no seconds". Hours keep counting past 24
 * ("37h 32m", not "1d 13h") so every chip reads in the same unit.
 *   0 → "0m", 59 → "59m", 60 → "1h", 61 → "1h 1m", 2252 → "37h 32m"
 * Negative or non-finite input returns "0m": a duration we cannot trust is
 * never rendered as a number that looks real.
 */
export function formatDurationHM(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return "0m";
  const m = Math.floor(totalMinutes);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}m`;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}
